import { z } from 'zod';
import { nutritionSchema } from './canteen-operations.js';
const uuidSchema = z.string().uuid();
const dateSchema = z.string().date();
const dateTimeSchema = z.string().datetime({ offset: true });
const emptyNutrition = {
    energyKj: null,
    energyKcal: null,
    fatG: null,
    saturatedFatG: null,
    carbohydratesG: null,
    sugarsG: null,
    fibreG: null,
    proteinG: null,
    saltG: null,
};
export const canteenOfferItemSchema = z.object({
    id: uuidSchema,
    canteenId: uuidSchema,
    serviceDate: dateSchema,
    itemType: z.object({
        id: uuidSchema,
        code: z.string().min(2).max(50),
        name: z.string().min(1).max(120),
        sequence: z.number().int().nonnegative(),
    }),
    variantCode: z.string().min(1).max(50),
    name: z.string().min(1).max(200),
    description: z.string().max(1000),
    serviceWindow: z.object({
        startsAt: dateTimeSchema,
        endsAt: dateTimeSchema,
    }),
    unitPriceMinor: z.number().int().nonnegative(),
    currency: z.string().regex(/^[A-Z]{3}$/),
    allergens: z.array(z.string().min(1).max(100)),
    possibleAllergens: z.array(z.string().min(1).max(100)).default([]),
    recipeVersionId: uuidSchema.nullable().default(null),
    nutrition: nutritionSchema.default(emptyNutrition),
    diet: z.string().max(100).default(''),
    availableQuantity: z.number().int().nonnegative().nullable(),
    remainingQuantity: z.number().int().nonnegative().nullable(),
    reservationState: z.enum(['open', 'closed', 'sold_out']),
    reservationClosesAt: dateTimeSchema,
    state: z.literal('published'),
    version: z.number().int().positive(),
});
export const canteenOfferRangeSchema = z.object({
    contractVersion: z.literal('1'),
    institutionId: uuidSchema,
    canteenId: uuidSchema,
    startsOn: dateSchema,
    endsOn: dateSchema,
    timeZone: z.string().min(1).max(100),
    generatedAt: dateTimeSchema,
    items: z.array(canteenOfferItemSchema),
});
export const createSelfReservationSchema = z
    .object({
    contractVersion: z.literal('1'),
    canteenId: uuidSchema,
    consumerId: uuidSchema.optional(),
    serviceDate: dateSchema,
    lines: z
        .array(z.object({
        offerItemVersionId: uuidSchema,
        quantity: z.number().int().min(1).max(20),
    }))
        .min(1)
        .max(50),
})
    .superRefine((request, context) => {
    const ids = request.lines.map((line) => line.offerItemVersionId);
    if (new Set(ids).size !== ids.length) {
        context.addIssue({
            code: 'custom',
            message: 'Pozycja oferty może wystąpić tylko raz.',
            path: ['lines'],
        });
    }
});
/**
 * A person resigns from whole or selected not-yet-issued portions. The
 * resulting financial adjustment is an immutable fact; it never deletes the
 * original reservation.
 */
export const cancelSelfReservationSchema = z.object({
    reason: z.string().trim().min(3).max(500).default('Rezygnacja własna'),
    lines: z
        .array(z.object({
        reservationLineId: uuidSchema,
        quantity: z.number().int().positive().max(10_000),
    }))
        .max(100)
        .nullable()
        .default(null),
});
export const dietaryCompatibilitySnapshotSchema = z.object({
    status: z.enum(['compatible', 'warning', 'block', 'missing_data']),
    matchedRestrictionIds: z.array(uuidSchema),
    profileVersionId: uuidSchema.nullable(),
    offer: z.object({
        allergens: z.array(z.string().min(1).max(100)),
        possibleAllergens: z.array(z.string().min(1).max(100)),
        diet: z.string().max(100),
    }),
});
export const selfReservationSummarySchema = z.object({
    id: uuidSchema,
    consumerId: uuidSchema,
    consumerDisplayName: z.string().min(1).max(200).default('Osoba żywiona'),
    canteenId: uuidSchema,
    serviceDate: dateSchema,
    state: z.enum(['confirmed', 'partially_issued', 'fulfilled', 'cancelled']),
    totalAmountMinor: z.number().int().nonnegative(),
    currency: z.string().regex(/^[A-Z]{3}$/),
    version: z.number().int().positive(),
    createdAt: dateTimeSchema,
    lines: z.array(z.object({
        id: uuidSchema,
        offerItemVersionId: uuidSchema,
        name: z.string().min(1).max(200),
        quantity: z.number().int().positive(),
        unitPriceMinor: z.number().int().nonnegative(),
        amountMinor: z.number().int().nonnegative(),
        cancelledQuantity: z.number().int().nonnegative().default(0),
        issuedQuantity: z.number().int().nonnegative().default(0),
        pricing: z
            .object({
            baseUnitPriceMinor: z.number().int().nonnegative(),
            resolvedUnitPriceMinor: z.number().int().nonnegative(),
            ruleId: uuidSchema.nullable(),
            ruleCode: z.string().nullable(),
            ruleVersion: z.number().int().positive().nullable(),
            ruleScope: z
                .enum(['default', 'person', 'group'])
                .nullable()
                .default(null),
            matchedGroupIds: z.array(uuidSchema).default([]),
            audienceSnapshotHash: z
                .string()
                .regex(/^sha256:[a-f0-9]{64}$/)
                .nullable()
                .default(null),
            audienceObservedAt: dateTimeSchema.nullable().default(null),
        })
            .default({
            baseUnitPriceMinor: 0,
            resolvedUnitPriceMinor: 0,
            ruleId: null,
            ruleCode: null,
            ruleVersion: null,
            ruleScope: null,
            matchedGroupIds: [],
            audienceSnapshotHash: null,
            audienceObservedAt: null,
        }),
        dietary: dietaryCompatibilitySnapshotSchema.default({
            status: 'missing_data',
            matchedRestrictionIds: [],
            profileVersionId: null,
            offer: { allergens: [], possibleAllergens: [], diet: '' },
        }),
    })),
});
export const canteenReservationConfirmedEventSchema = z.object({
    eventId: uuidSchema,
    eventType: z.literal('canteen.reservation.confirmed.v1'),
    contractVersion: z.literal('1'),
    institutionId: uuidSchema,
    canteenId: uuidSchema,
    reservationId: uuidSchema,
    reservationVersion: z.number().int().positive(),
    consumerId: uuidSchema,
    serviceDate: dateSchema,
    lines: z
        .array(z.object({
        offerItemVersionId: uuidSchema,
        name: z.string().min(1).max(200),
        quantity: z.number().int().positive(),
        unitPriceMinor: z.number().int().nonnegative(),
        amountMinor: z.number().int().nonnegative(),
    }))
        .min(1),
    totalAmountMinor: z.number().int().nonnegative(),
    currency: z.string().regex(/^[A-Z]{3}$/),
    correlationId: z.string().min(1).max(200),
    occurredAt: dateTimeSchema,
});
export const canteenItemTypeSchema = z.object({
    id: uuidSchema,
    canteenId: uuidSchema,
    code: z.string().regex(/^[a-z][a-z0-9_-]{1,49}$/),
    name: z.string().trim().min(1).max(120),
    sequence: z.number().int().min(0).max(10_000),
    version: z.number().int().positive(),
    activeFrom: dateTimeSchema,
    activeUntil: dateTimeSchema.nullable(),
});
/** A versioned price decision. Person rules take precedence over defaults. */
export const canteenPriceRuleSchema = z.object({
    id: uuidSchema,
    canteenId: uuidSchema,
    code: z.string().regex(/^[a-z][a-z0-9_-]{1,49}$/),
    version: z.number().int().positive(),
    scope: z.enum(['default', 'person', 'group']),
    targetId: uuidSchema.nullable(),
    itemTypeId: uuidSchema.nullable(),
    adjustment: z.enum(['fixed_price', 'discount', 'surcharge']),
    amountMinor: z.number().int().nonnegative(),
    priority: z.number().int().min(0).max(10_000),
    effectiveFrom: dateTimeSchema,
    effectiveUntil: dateTimeSchema.nullable(),
    active: z.boolean(),
});
export const upsertCanteenPriceRuleSchema = z
    .object({
    contractVersion: z.literal('1'),
    code: z
        .string()
        .trim()
        .toLowerCase()
        .regex(/^[a-z][a-z0-9_-]{1,49}$/),
    scope: z.enum(['default', 'person', 'group']),
    targetId: uuidSchema.nullable().default(null),
    itemTypeId: uuidSchema.nullable().default(null),
    adjustment: z.enum(['fixed_price', 'discount', 'surcharge']),
    amountMinor: z.number().int().nonnegative().max(10_000_000),
    priority: z.number().int().min(0).max(10_000).default(0),
    effectiveFrom: dateTimeSchema,
    effectiveUntil: dateTimeSchema.nullable().default(null),
})
    .superRefine((rule, context) => {
    if (rule.scope === 'default' && rule.targetId !== null)
        context.addIssue({
            code: 'custom',
            path: ['targetId'],
            message: 'Stawka domyślna nie wskazuje odbiorcy.',
        });
    if (rule.scope !== 'default' && rule.targetId === null)
        context.addIssue({
            code: 'custom',
            path: ['targetId'],
            message: 'Stawka osoby lub grupy wymaga wskazania odbiorcy.',
        });
    if (rule.effectiveUntil !== null &&
        rule.effectiveUntil <= rule.effectiveFrom)
        context.addIssue({
            code: 'custom',
            path: ['effectiveUntil'],
            message: 'Koniec obowiązywania musi być późniejszy od początku.',
        });
});
export const canteenPeriodSchema = z.object({
    id: uuidSchema,
    canteenId: uuidSchema,
    code: z.string().regex(/^[a-z0-9][a-z0-9_-]{1,49}$/),
    startsOn: dateSchema,
    endsOn: dateSchema,
    state: z.enum(['open', 'closing', 'closed', 'corrected']),
    snapshot: z.record(z.string(), z.unknown()).nullable(),
    closedAt: dateTimeSchema.nullable(),
    closedBy: uuidSchema.nullable(),
    correctionReason: z.string().nullable(),
});
export const createCanteenPeriodSchema = z
    .object({
    contractVersion: z.literal('1'),
    code: z
        .string()
        .trim()
        .toLowerCase()
        .regex(/^[a-z0-9][a-z0-9_-]{1,49}$/),
    startsOn: dateSchema,
    endsOn: dateSchema,
})
    .refine((period) => period.startsOn <= period.endsOn, {
    path: ['endsOn'],
    message: 'Koniec okresu nie może być wcześniejszy od początku.',
});
export const closeCanteenPeriodSchema = z.object({
    reason: z.string().trim().min(3).max(500),
});
export const correctCanteenPeriodSchema = z.object({
    reason: z.string().trim().min(3).max(500),
});
export const canteenAdminConfigSchema = z.object({
    contractVersion: z.literal('1'),
    institutionId: uuidSchema,
    canteenId: uuidSchema,
    canteenName: z.string().min(1).max(200),
    currency: z.string().regex(/^[A-Z]{3}$/),
    timeZone: z.string().min(1).max(100),
    itemTypes: z.array(canteenItemTypeSchema),
    priceRules: z.array(canteenPriceRuleSchema).default([]),
    periods: z.array(canteenPeriodSchema).default([]),
});
export const upsertCanteenItemTypeSchema = z.object({
    contractVersion: z.literal('1'),
    code: z
        .string()
        .trim()
        .toLowerCase()
        .regex(/^[a-z][a-z0-9_-]{1,49}$/),
    name: z.string().trim().min(1).max(120),
    sequence: z.number().int().min(0).max(10_000),
});
export const menuPublicationLineSchema = z.object({
    itemTypeId: uuidSchema,
    variantCode: z
        .string()
        .trim()
        .toLowerCase()
        .regex(/^[a-z][a-z0-9_-]{0,49}$/),
    name: z.string().trim().min(1).max(200),
    description: z.string().trim().max(1000).default(''),
    serviceStartsAt: z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/),
    serviceEndsAt: z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/),
    unitPriceMinor: z.number().int().min(0).max(10_000_000),
    allergens: z.array(z.string().trim().min(1).max(100)).max(50).default([]),
    possibleAllergens: z
        .array(z.string().trim().min(1).max(100))
        .max(50)
        .default([]),
    recipeVersionId: uuidSchema.nullable().default(null),
    diet: z.string().trim().max(100).default(''),
    availableQuantity: z.number().int().nonnegative().nullable().default(null),
});
export const menuPublicationDraftSchema = z
    .object({
    contractVersion: z.literal('1'),
    canteenId: uuidSchema,
    serviceDate: dateSchema,
    lines: z.array(menuPublicationLineSchema).min(1).max(100),
})
    .superRefine((value, context) => {
    const keys = value.lines.map((line) => `${line.itemTypeId}:${line.variantCode}`);
    if (new Set(keys).size !== keys.length) {
        context.addIssue({
            code: 'custom',
            message: 'Typ posiłku i wariant mogą wystąpić tylko raz.',
            path: ['lines'],
        });
    }
    value.lines.forEach((line, index) => {
        if (line.serviceStartsAt >= line.serviceEndsAt) {
            context.addIssue({
                code: 'custom',
                message: 'Koniec wydawania musi być późniejszy od początku.',
                path: ['lines', index, 'serviceEndsAt'],
            });
        }
    });
});
export const canteenMenuPublicationPreviewSchema = z.object({
    contractVersion: z.literal('1'),
    previewHash: z.string().regex(/^[a-f0-9]{64}$/),
    serviceDate: dateSchema,
    canPublish: z.boolean(),
    replacesPublishedItems: z.number().int().nonnegative(),
    affectedReservations: z.number().int().nonnegative(),
    affectedPortions: z.number().int().nonnegative(),
    newItems: z.number().int().positive(),
    totalCatalogValueMinor: z.number().int().nonnegative(),
    currency: z.string().regex(/^[A-Z]{3}$/),
    warnings: z.array(z.string().min(1).max(500)),
});
export const publishMenuSchema = menuPublicationDraftSchema.and(z.object({ previewHash: z.string().regex(/^[a-f0-9]{64}$/) }));
export const menuPublicationResultSchema = z.object({
    contractVersion: z.literal('1'),
    serviceDate: dateSchema,
    publishedItems: z.number().int().positive(),
    retiredItems: z.number().int().nonnegative(),
    publishedAt: dateTimeSchema,
});
